import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UploadArtwork from '../Images/UploadArtwork.png';
import edit from '../Images/edit-text.png';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import { Pagination, Autoplay } from 'swiper/modules';

const SavedItems = ({ token }) => {
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null); // For expand/collapse rows
  const [showConfirmModal, setShowConfirmModal] = useState(false); // For delete confirmation modal
  const [selectedItem, setSelectedItem] = useState(null); // Track item selected for deletion
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [filesToUpload, setFilesToUpload]     = useState({ file1: null, file2: null });
  const [uploadError, setUploadError] = useState({});
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reorderMessage, setReorderMessage] = useState("");
  const [showReorderPopup, setShowReorderPopup] = useState(false);
  
  const navigate = useNavigate();

  // Fetch saved items from API
  const fetchSavedItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        "https://backend.printtrio.co.uk/api/saved-item/user-saved-item",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSavedItems(data.data || []);
      } else {
        setError("Failed to fetch saved items.");
      }
    } catch (err) {
      setError("An error occurred while fetching saved items.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSavedItems();
    }
  }, [token]);

  // Open confirmation modal
  const openConfirmModal = (itemId) => {
    setSelectedItem(itemId);
    setShowConfirmModal(true);
  };

  // Handle item deletion
  const handleDeleteItem = async (itemId) => {
    try {
      const response = await fetch(
        `https://backend.printtrio.co.uk/api/saved-item/delete-item/${itemId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setSavedItems((prevItems) =>
          prevItems.filter((item) => item.id !== itemId)
        );
      } else {
        console.error("Failed to delete item.");
      }
    } catch (err) {
      console.error("Error deleting item:", err);
    } finally {
      setShowConfirmModal(false);
      setSelectedItem(null);
    }
  };

  const toggleExpandRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleEditClick = (id, productId) => {
    // Navigate to '/product' and pass state
    navigate('/EditProductAfterSaved', {   state: { 
      id,
      productId,
      fromSaved: true    
    } });
  };

  const handlePlaceOrder = async (itemId) => {
    try {
      // API call to add item to the cart
      const response = await fetch(
        "https://backend.printtrio.co.uk/api/saved-item/add-to-cart-from-saved-item",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            item_id: itemId, // Pass the item ID in the request body
          }),
        }
      );
  
      const data = await response.json();
  
      if (data.success) {
        fetchSavedItems();
        setReorderMessage("Your item was successfully re-added to the cart!");
        setShowReorderPopup(true);
      } else {
        console.error("Failed to add item to the cart:", data.message || "Unknown error");
        alert("Failed to add the item to the cart. Please try again.");
      }
    } catch (error) {
      console.error("Error adding item to the cart:", error);
      alert("An error occurred while adding the item to the cart.");
    }
  };



  
// Simplify signature: (file1, file2, itemId)
const uploadFiles = async (file1, file2, itemId) => {
  const formData = new FormData();
  formData.append("file1", file1 || "");
  formData.append("file2", file2 || "");

  const res = await fetch(
    `https://backend.printtrio.co.uk/api/saved-item/upload-file/${itemId}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  );

  const data = await res.json();
  if (res.ok && data.success) {
    // refresh your list here instead
    await fetchSavedItems();
  } else {
    // refresh your list so the new file1/url appears
    await fetchSavedItems();
  }
};

  
  
  
  
  
  const handleFileChange = (e) => {
    const selectedFiles = e.target.files; // Get selected files
    setFilesToUpload({
      file1: selectedFiles[0] || null, // First selected file (if exists)
      file2: selectedFiles[1] || null, // Second selected file (if exists)
    });
  };
  
  
  
  const handleUploadFiles = async () => {
    if (!filesToUpload.file1 && !filesToUpload.file2) {
      setUploadErrorMessage("Please select at least one file");
      return;
    }
    if (!selectedProduct) return;  // selectedProduct = the saved‐item’s id
  
    setIsUploading(true);
    await uploadFiles(
      filesToUpload.file1,
      filesToUpload.file2,
      selectedProduct      // ← this must be item.id from fetchSavedItems
    );
    await fetchSavedItems();
    setIsUploading(false);
    setShowUploadModal(false);
    setFilesToUpload({ file1: null, file2: null });
    
  };

  
  if (loading) {
    return (
      <div className="flex-grow p-10 bg-white mt-[3%] mb-[3%] mx-[5%] text-center py-10">
        Loading saved items...
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  // if (savedItems.length === 0) {
  //   return (
  //     <div className="flex flex-col justify-center items-center text-center">
  //       <p className="text-gray-500 text-sm mb-6">
  //         No saved items found. Start saving your favorite products now!
  //       </p>
  //       <button
  //         className="bg-transparent text-[#EC008C] font-semibold border-2 border-[#EC008C] rounded-full px-6 py-3 hover:bg-[#EC008C] hover:text-white transition duration-300 flex items-center gap-2"
  //         onClick={() => navigate("/")}
  //       >
  //         Shop Now
  //         <svg
  //           xmlns="http://www.w3.org/2000/svg"
  //           className="h-5 w-5"
  //           fill="none"
  //           viewBox="0 0 24 24"
  //           stroke="currentColor"
  //           strokeWidth={2}
  //         >
  //           <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  //         </svg>
  //       </button>
  //     </div>
  //   );
  // }

  return (
    <div className="flex-grow p-10 bg-white mt-[3%] mb-[3%] mx-[5%] rounded-md overflow-x-auto">
    {savedItems.length === 0 ? (
      // No Orders Found View
      <div className="flex items-center justify-center min-h-[50vh] w-full">
      <div className="text-center">
        <p className="text-gray-500 text-sm mb-6">
          No active orders found. Check out our collection and start shopping!
        </p>
        <button
          className="bg-transparent text-[#EC008C] font-semibold border-2 border-[#EC008C] rounded-full px-6 py-3 hover:bg-[#EC008C] hover:text-white transition duration-300 flex items-center gap-2 mx-auto"
          onClick={() => navigate("/")}
        >
          Shop Now
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
    
    
    ) : (
      <div className="">
        <table className="w-full border-separate border-spacing-y-0">
          <thead>
            <tr className="bg-white text-left">
              <th className="py-2 px-4">No</th>
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Created Date</th>
              <th className="py-2 px-4">Price</th>
              <th className="py-2 px-4 text-right">
                <span className="inline-block w-4 h-4"></span>
              </th>
            </tr>
          </thead>
          <tbody>
          {savedItems.map((item, idx) => {
  const isExpanded = expandedRow === item.id;
  const isSample = item.is_free_sample === 1;
  const display = isSample ? item.free_sample : item.product;
  const price = isSample
    ? parseFloat(item.free_sample.price)
    : parseFloat(item.total_price_with_vat || 0);

  return (
    <React.Fragment key={item.id}>
      {/* Main Row */}
      <tr className="text-left bg-[#F1F8FE] rounded-md">
        <td className="py-4 px-4 align-middle font-medium">
          {`#${idx + 1}`}
        </td>
        <td className="py-4 px-4 align-middle">
          {display?.name}
        </td>
        <td className="py-4 px-4 align-middle">
          {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
        </td>
        <td className="py-4 px-4 align-middle">
          £{price.toLocaleString("en-GB", { minimumFractionDigits: 2 })}
        </td>
        <td className="py-4 px-4 align-middle text-right">
  <button onClick={() => toggleExpandRow(item.id)}>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      className={`h-5 w-5 inline-block transform transition-transform ${
        isExpanded ? 'rotate-180' : ''
      }`}
      fill="none"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 10l7 7 7-7"
      />
    </svg>
  </button>
</td>
      </tr>

      {/* Expanded Details Row */}
      {isExpanded && (
        <tr className="bg-[#F1F8FE] rounded-md">
          <td colSpan="8" className="p-4">
            <div className="flex flex-col gap-4">
              

              {/* Image / Swiper / Sample */}
              <div className="flex gap-4">
                <div className="relative w-24 h-24 flex-shrink-0">
                  {item.file1 || item.file2 ? (
                    item.file1 && item.file2 ? (
                      <Swiper
                        modules={[Pagination, Autoplay]}
                        pagination={{ clickable: true }}
                        spaceBetween={30}
                        slidesPerView={1}
                        autoplay={false}
                        speed={2000}
                        loop={false}
                        className="w-full h-full"
                      >
                        <SwiperSlide>
                          <div className="relative w-full h-full">
                            <img
                              src={`https://backend.printtrio.co.uk/${item.file1}`}
                              alt={display?.name || ""}
                              className="object-contain rounded-lg w-full h-full"
                            />
                          </div>
                        </SwiperSlide>
                        <SwiperSlide>
                          <div className="relative w-full h-full">
                            <img
                              src={`https://backend.printtrio.co.uk/${item.file2}`}
                              alt={display?.name || ""}
                              className="object-contain rounded-lg w-full h-full"
                            />
                          </div>
                        </SwiperSlide>
                      </Swiper>
                    ) : (
                      <div className="relative w-full h-full">
                        <img
                          src={`https://backend.printtrio.co.uk/${item.file1 || item.file2}`}
                          alt={display?.name || ""}
                          className="object-contain rounded-lg w-full h-full"
                        />
                      </div>
                    )
                  ) : isSample ? (
                    <img
                      src={`https://backend.printtrio.co.uk/${display.image}`}
                      alt={display.name}
                      className="object-contain rounded-lg w-full h-full"
                      style={{ cursor: "default" }}
                    />
                  ) : (
                    <img
                      src={UploadArtwork}
                      alt=""
                      className="object-contain rounded-lg w-full h-full cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(item.id);
                        setShowUploadModal(true);
                      }}
                    />
                  )}

 {!isSample && (item.file1 || item.file2) && (
   <button
     onClick={() => {
       setSelectedProduct(item.id);
       setShowUploadModal(true);
     }}
     className="absolute top-1 right-1 w-5 h-5 z-10"      // <<< add z-10
     title="Upload artwork"
   >
     <img
       src={edit}
       alt="Edit"
       className="w-full h-full object-cover"
     />
   </button>
)}

                  {uploadError[item.id] && (
                    <p
                      className="text-red-500 text-sm mt-2 cursor-pointer"
                      onClick={() => {
                        setSelectedProduct(item.id);
                        setShowUploadModal(true);
                      }}
                    >
                      {uploadError[item.id]}
                    </p>
                  )}
                </div>

                {/* Details */}
                <div className="text-sm text-gray-700 space-y-1">
  {/* Sample */}
  {isSample ? (
    <>
      <p className="mt-9"><strong>{display.name}</strong></p>
      {/* <p>{display.description}</p> */}
    </>
  ) : (
    <>
      <p><strong>Size:</strong> {item.size || 'N/A'}</p>
      <p><strong>Quantity:</strong> {item.quantity || 'N/A'}</p>
      <p><strong>Paper Type:</strong> {item.paper_type || 'N/A'}</p>
      <p><strong>Paper Weight:</strong> {item.paper_weight || 'N/A'}</p>

      {item.foiling_color && (
        <p><strong>Foiling Color:</strong> {item.foiling_color}</p>
      )}
      {item.spot_UV === 1 && (
        <p><strong>Spot UV:</strong> Yes</p>
      )}
      {item.white_color === 1 && (
        <p><strong>White Color:</strong> Yes</p>
      )}
      {item.lamination && (
        <p><strong>Lamination:</strong> {item.lamination}</p>
      )}
      {item.corner_cut_style && (
        <p><strong>Corner Cut Style:</strong> {item.corner_cut_style}</p>
      )}
      {item.orientation && (
        <p><strong>Orientation:</strong> {item.orientation}</p>
      )}
      {item.printed_side && (
        <p><strong>Printed Side:</strong> {item.printed_side}</p>
      )}

      <p>
        <strong>Service Description:</strong>{" "}
        {item.service_description?.trim() || 'No notes provided.'}
      </p>
    </>
  )}
</div>

              </div>

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  {!isSample && (
                    <>
                      <button
                        onClick={() => handleEditClick(item.id, item.product_id)}
                        className="flex items-center gap-1 text-gray-700 hover:text-black"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => openConfirmModal(item.id)}
                        className="flex items-center gap-1 text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={() => handlePlaceOrder(item.id)}
                  className="mt-2 px-4 py-2 border-2 border-[#EC008C] text-[#EC008C] rounded-full text-sm font-semibold hover:bg-[#EC008C] hover:text-white transition duration-300"
                >
                  Add to cart
                </button>
              </div>

            </div>
          </td>
        </tr>
      )}

{idx < savedItems.length - 1 && (
          <tr>
            <td colSpan="5" className="p-0 bg-white">
              <div className="h-4" />
            </td>
          </tr>
        )}
    </React.Fragment>
  );
})}

          </tbody>
        </table>
      </div>
 )}

{showUploadModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold mb-4 text-center">Upload Files</h2>
      <p className="text-gray-600 mb-4 text-center">Upload the required files for this product.</p>

      <input
        type="file"
        multiple
        onChange={handleFileChange} // Handle file selection
        className="block w-full mb-4"
      />

      {uploadErrorMessage && (
        <p className="text-red-500 text-sm mb-4">{uploadErrorMessage}</p>
      )}

      <div className="flex justify-end space-x-4">
      {isUploading ? (
    // Show spinner during upload
    <div className="flex justify-center w-full">
      <svg
        className="animate-spin h-12 w-12 text-pink-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v8H4z"
        />
      </svg>
    </div>
  ) : (
    <>
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          onClick={() => setShowUploadModal(false)} // Close the modal
        >
          Cancel
        </button>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          onClick={handleUploadFiles} // Trigger file upload
        >
          Upload
        </button>
        </>
  )}
      </div>
    </div>
  </div>
)}
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-md shadow-md">
            <h2 className="text-lg font-bold mb-4">Confirm Deletion</h2>
            <p className="mb-6">Are you sure you want to delete this item?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteItem(selectedItem)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
{showReorderPopup && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded shadow-lg text-center">
      <p className="mb-4">{reorderMessage}</p>
      <button
        className="px-4 py-2 bg-[#EC008C] text-white rounded"
        onClick={() => setShowReorderPopup(false)}
      >
        Close
      </button>
    </div>
  </div>
)}


    </div>
  );
};

export default SavedItems;
